import React, { useEffect, useMemo, useState, useRef } from "react";
import { Button, Grid, TextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazySearchLoadingsQuery } from "store/services/shipmentLoadingApi";
import { useAuth } from "hooks";
import { getDateTimeAuto } from "utils/dateFormats";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";

const INITIAL_STATE = {
  account: null,
  entity: null,
  loadingId: null,
  consolidationId: null,
  collectionId: null,
  shipmentId: [],
  unloadingTime: getDateTimeAuto(),
  unloadingLocation: { lat: null, lon: null },
  isActive: true,
  unloadingBranch: null,
};

const INITIAL_PAGE_SIZE = 10;

const ShipmentUnLoadingForm = ({
  isBusy = null,
  shipmentUnLoadingDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const [loadingSearchQuery, setLoadingSearchQuery] = useState("");
  const [shipmentSearchQuery, setShipmentSearchQuery] = useState("");
  const [loadingOptions, setLoadingOptions] = useState([]);
  const [shipmentOptions, setShipmentOptions] = useState([]);
  const [initialLoadingsFetched, setInitialLoadingsFetched] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity?._id ? viewEntity.entity : null,
      account: viewEntity?.entity?.account?._id ? viewEntity.entity.account : null,
      loadingId: state?.loading || null,
      shipmentId: state?.shipments || [],
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchLoadingId = useWatch({ control, name: "loadingId" });

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [searchLoadings, { isLoading: loadingSearchResults }] =
    useLazySearchLoadingsQuery();
  const [
    fetchBranchList,
    { data: branchListData, isLoading: loadingBranchList },
  ] = useLazyGetDDLBranchListQuery();

  // Keep the latest account/entity in refs so the debounced fns below
  // always read fresh values without needing to be re-created on every render.
  const accountRef = useRef(_watchAccount);
  const entityRef = useRef(_watchEntity);
  useEffect(() => {
    accountRef.current = _watchAccount;
    entityRef.current = _watchEntity;
  }, [_watchAccount, _watchEntity]);

  // Fetch initial page (10 records) when entity is available
  const fetchInitialLoadings = async () => {
    if (!_watchAccount?._id || !_watchEntity?._id) return;

    try {
      const result = await searchLoadings({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        query: "",
        limit: INITIAL_PAGE_SIZE,
      }).unwrap();

      const loadings = result?.data || [];
      setLoadingOptions(loadings);

      const allShipments = loadings.flatMap((loading) => {
        return (loading.shipmentId || []).map((shipment) => ({
          ...shipment,
          _loadingId: loading._id,
          _loadingData: loading,
        }));
      });

      setShipmentOptions(allShipments);
      setInitialLoadingsFetched(true);
    } catch (error) {
      console.error("Error fetching initial loadings:", error);
      setLoadingOptions([]);
      setShipmentOptions([]);
    }
  };

  // Search for loadings by loadingId
  const handleLoadingSearch = async (query) => {
    if (!query || query.length < 4) {
      if (initialLoadingsFetched) {
        fetchInitialLoadings();
      }
      return;
    }

    const account_ = accountRef.current;
    const entity_ = entityRef.current;

    if (!account_?._id || !entity_?._id) {
      toast.error("Please select account and entity first");
      return;
    }

    try {
      const result = await searchLoadings({
        account: account_._id,
        entity: entity_._id,
        query: query.trim(),
        limit: 25,
      }).unwrap();

      setLoadingOptions(result?.data || []);
    } catch (error) {
      console.error("Error searching loadings:", error);
      setLoadingOptions([]);
    }
  };

  // Search for shipments by shipmentId
  const handleShipmentSearch = async (query) => {
    if (!query || query.length < 4) {
      if (initialLoadingsFetched) {
        fetchInitialLoadings();
      }
      return;
    }

    const account_ = accountRef.current;
    const entity_ = entityRef.current;

    if (!account_?._id || !entity_?._id) {
      toast.error("Please select account and entity first");
      return;
    }

    try {
      const result = await searchLoadings({
        account: account_._id,
        entity: entity_._id,
        query: query.trim(),
        limit: 25,
      }).unwrap();

      const allShipments = (result?.data || []).flatMap((loading) => {
        return (loading.shipmentId || []).map((shipment) => ({
          ...shipment,
          _loadingId: loading._id,
          _loadingData: loading,
        }));
      });

      setShipmentOptions(allShipments);
    } catch (error) {
      console.error("Error searching shipments:", error);
      setShipmentOptions([]);
    }
  };

  // Debounced wrappers — created once, stable across renders.
  const debouncedLoadingSearch = useMemo(
    () => debounce((q) => handleLoadingSearch(q), 350),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const debouncedShipmentSearch = useMemo(
    () => debounce((q) => handleShipmentSearch(q), 350),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    return () => {
      debouncedLoadingSearch.cancel();
      debouncedShipmentSearch.cancel();
    };
  }, [debouncedLoadingSearch, debouncedShipmentSearch]);

  // Handle loading selection
  const handleLoadingSelection = (selectedLoading) => {
    if (!selectedLoading) {
      setValue("loadingId", null);
      setValue("shipmentId", []);
      return;
    }

    setValue("loadingId", selectedLoading);

    const loadedShipments = (selectedLoading.shipmentId || []).filter(
      (shipment) =>
        shipment.status === "Loaded" &&
        shipment.isOnHold !== true &&
        shipment.isConsolidated === true
    );

    setValue("shipmentId", loadedShipments, { shouldValidate: true });
  };

  // Handle shipment selection
  const handleShipmentSelection = (selectedShipments) => {
    if (!selectedShipments || selectedShipments.length === 0) {
      setValue("shipmentId", []);
      return;
    }

    if (_watchLoadingId) {
      setValue("shipmentId", selectedShipments, { shouldValidate: true });
      return;
    }

    const hasLoadingData = selectedShipments.some(shipment => shipment._loadingId);

    if (hasLoadingData) {
      const firstShipmentWithLoading = selectedShipments.find(s => s._loadingId);
      const loadingFromShipment = firstShipmentWithLoading._loadingData;
      const loadingIdFromShipment = firstShipmentWithLoading._loadingId;

      const allFromSameLoading = selectedShipments
        .filter(shipment => shipment._loadingId)
        .every((shipment) => shipment._loadingId === loadingIdFromShipment);

      if (!allFromSameLoading) {
        toast.error("Cannot select shipments from different loadings");
        return;
      }

      setValue("loadingId", loadingFromShipment);

      const cleanShipments = selectedShipments.map((shipment) => {
        const { _loadingId, _loadingData, ...cleanShipment } = shipment;
        return cleanShipment;
      });

      setValue("shipmentId", cleanShipments, { shouldValidate: true });
    } else {
      setValue("shipmentId", selectedShipments, { shouldValidate: true });
    }
  };

  const availableShipments = useMemo(() => {
    if (_watchLoadingId?.shipmentId) {
      return (_watchLoadingId.shipmentId || []).filter(
        (shipment) =>
          (shipment.status === "Loaded" || shipment.status === "Return") &&
          shipment.isOnHold !== true &&
          shipment.isConsolidated === true
      );
    }
    return shipmentOptions;
  }, [_watchLoadingId, shipmentOptions]);

  useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id && !shipmentUnLoadingDetail && !initialLoadingsFetched) {
      fetchInitialLoadings();
    }
  }, [_watchAccount?._id, _watchEntity?._id, shipmentUnLoadingDetail]);

  useEffect(() => {
    const accountId = _watchAccount?._id || viewEntity?.entity?.account?._id;
    const entityId = _watchEntity?._id || viewEntity?.entity?._id;
    if (accountId && entityId) {
      fetchBranchList({ account: accountId, entity: entityId });
    }
  }, [_watchAccount?._id, _watchEntity?._id, viewEntity, fetchBranchList]);
  const availableUnloadingBranches = useMemo(() => branchListData || [], [branchListData]);

  useEffect(() => {
    if (state?.loading) {
      setValue("loadingId", state.loading);
      if (state.loading.shipmentId) {
        const loadedShipments = state.loading.shipmentId.filter(
          (shipment) =>
            shipment.status === "Loaded" &&
            shipment.isOnHold !== true &&
            shipment.isConsolidated === true
        );
        setValue("shipmentId", loadedShipments);
      }
    }

    if (state?.shipments?.length) {
      setValue("shipmentId", state.shipments);
    }
  }, [state, setValue]);

  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  useEffect(() => {
    if (_watchEntity?._id) {
      setInitialLoadingsFetched(false);
      setLoadingOptions([]);
      setShipmentOptions([]);
    }
  }, [_watchEntity?._id]);

  useEffect(() => {
    if (!shipmentUnLoadingDetail) return;
    reset({
      ...INITIAL_STATE,
      ...shipmentUnLoadingDetail,
      unloadingTime: shipmentUnLoadingDetail?.unloadingTime
        ? new Date(shipmentUnLoadingDetail.unloadingTime).toISOString().slice(0, 16)
        : getDateTimeAuto(),
      consolidationId: shipmentUnLoadingDetail?.consolidationId || null,
      collectionId: shipmentUnLoadingDetail?.collectionId || null,
      shipmentId:
        shipmentUnLoadingDetail?.shipmentId?.map((item) => item.shipment) || [],
      account:
        shipmentUnLoadingDetail?.account?._id
          ? shipmentUnLoadingDetail.account
          : null,
      entity:
        shipmentUnLoadingDetail?.entity?._id ? shipmentUnLoadingDetail.entity : null,
      loadingId: Array.isArray(shipmentUnLoadingDetail?.loadingId)
        ? shipmentUnLoadingDetail.loadingId
        : shipmentUnLoadingDetail?.loadingId
          ? [shipmentUnLoadingDetail.loadingId]
          : [],
      unloadingBranch:
        shipmentUnLoadingDetail?.unLoadedBranch ||
        shipmentUnLoadingDetail?.transitBranch ||
        null,
      unloadingLocation: {
        lat: shipmentUnLoadingDetail?.unloadingLocation?.lat || null,
        lon: shipmentUnLoadingDetail?.unloadingLocation?.lon || null,
      },
    });

    const accountId =
      shipmentUnLoadingDetail?.account?._id ||
      viewEntity?.entity?.account?._id ||
      null;
    if (accountId && (isAccountAdmin || isSystemAdmin)) {
      fetchEntities({ account: accountId });
    }
  }, [
    reset,
    shipmentUnLoadingDetail,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity,
  ]);

  const handleOnShipmentUnLoadingSubmit = (data) => {
    const shipmentId = data.shipmentId.map((shipment) => ({
      shipment: shipment._id,
      status: "Unloaded",
    }));

    const _shipmentUnLoadingPayload = {
      ...data,
      entity: data?.entity?._id || null,
      account: data?.account?._id || null,
      loadingId: data?.loadingId?._id || null,
      unloadingBranch: data.unloadingBranch?._id || null,
      shipmentId,
      unloadingStatus:
        data.shipmentId.length < (data.loadingId?.shipmentId?.length || 0)
          ? "Partial"
          : "Complete",
      unloadingLocation: {
        lat: data.unloadingLocation.lat ? Number(data.unloadingLocation.lat) : null,
        lon: data.unloadingLocation.lon ? Number(data.unloadingLocation.lon) : null,
      },
      collectionId: data.collectionId || null,
      consolidationId: data.consolidationId || null,
    };

    onSubmit(_shipmentUnLoadingPayload);
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                      setValue("loadingId", null);
                      setValue("consolidationId", null);
                      setValue("collectionId", null);
                      setValue("shipmentId", []);
                      setValue("unloadingLocation", { lat: null, lon: null });
                      setValue("unloadingBranch", null);
                      setLoadingOptions([]);
                      setShipmentOptions([]);
                      setInitialLoadingsFetched(false);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null || value === ""
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin || loadingAccounts}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("loadingId", null);
                      setValue("consolidationId", null);
                      setValue("collectionId", null);
                      setValue("shipmentId", []);
                      setValue("unloadingLocation", { lat: null, lon: null });
                      setValue("unloadingBranch", null);
                      setLoadingOptions([]);
                      setShipmentOptions([]);
                      setInitialLoadingsFetched(false);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null || value === ""
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    disabled={loadingEntities}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="loadingId"
              control={control}
              rules={{ required: "Please select ShipmentLoading ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  multiple={!!shipmentUnLoadingDetail}
                  label="Loading"
                  required
                  value={
                    shipmentUnLoadingDetail
                      ? Array.isArray(value) ? value : (value ? [value] : [])
                      : value ?? null
                  }
                  inputValue={loadingSearchQuery}
                  onInputChange={(_, newInputValue) => {
                    setLoadingSearchQuery(newInputValue);
                    if (newInputValue.length >= 4) {
                      debouncedLoadingSearch(newInputValue);
                    } else if (newInputValue.length === 0) {
                      debouncedLoadingSearch.cancel();
                      fetchInitialLoadings();
                    }
                  }}
                  onChange={(_, data) => {
                    onChange(data);
                    handleLoadingSelection(data);
                  }}
                  getOptionLabel={(option) =>
                    option?.loadingId && option?.vehicle_id?.registration_number
                      ? `${option.loadingId} - ${option.vehicle_id.vehicle_id}(${option.vehicle_id?.registration_number})`
                      : option?.loadingId || ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id || value === null
                  }
                  options={loadingOptions}
                  loading={loadingSearchResults}
                  loadingText="Loading..."
                  helperText={
                    errors?.loadingId?.message ||
                    "Type 4+ characters to search or select from list"
                  }
                  error={!!errors?.loadingId}
                  disabled={!!state?.loading || !!shipmentUnLoadingDetail}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select at least one Shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  multiple
                  label="Shipment"
                  required
                  value={value ?? []}
                  inputValue={shipmentSearchQuery}
                  onInputChange={(_, newInputValue) => {
                    setShipmentSearchQuery(newInputValue);
                    if (newInputValue.length >= 4 && !_watchLoadingId) {
                      debouncedShipmentSearch(newInputValue);
                    } else if (newInputValue.length === 0 && !_watchLoadingId) {
                      debouncedShipmentSearch.cancel();
                      fetchInitialLoadings();
                    }
                  }}
                  onChange={(_, data) => {
                    onChange(data);
                    handleShipmentSelection(data);
                  }}
                  getOptionLabel={(option) =>
                    option?.shipmentId
                      ? option.IsReturning || option.status === "Return"
                        ? `${option.shipmentId} (R)`
                        : option.shipmentId
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={availableShipments}
                  loading={loadingSearchResults}
                  loadingText="Loading..."
                  helperText={
                    errors?.shipmentId?.message ||
                    (_watchLoadingId
                      ? "Select from loaded shipments"
                      : "Type 4+ characters to search or select from list")
                  }
                  error={!!errors?.shipmentId}
                  disabled={!!state?.shipments || !!shipmentUnLoadingDetail}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="unloadingTime"
              control={control}
              defaultValue={INITIAL_STATE.unloadingTime}
              rules={{ required: "Unloading Time is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Unloading Time"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.unloadingTime}
                  helperText={errors?.unloadingTime?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="unloadingBranch"
              control={control}
              rules={{ required: "Please select an unloading branch." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Unloading Branch"
                  value={value || null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option ? `${option.name ?? ""} (${option.code ?? ""})` : ""
                  }
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={availableUnloadingBranches}
                  loading={loadingBranchList}
                  loadingText="Loading branches..."
                  helperText={errors?.unloadingBranch?.message}
                  error={!!errors?.unloadingBranch}
                  noOptionsText="No branches available"
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="unloadingLocation.lat"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Unloading Latitude (optional)"
                  type="number"
                  onChange={(e) => {
                    field.onChange(
                      e.target.value === "" ? null : Number(e.target.value)
                    );
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="unloadingLocation.lon"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Unloading Longitude (optional)"
                  type="number"
                  onChange={(e) => {
                    field.onChange(
                      e.target.value === "" ? null : Number(e.target.value)
                    );
                  }}
                />
              )}
            />
          </Grid>
          {shipmentUnLoadingDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnShipmentUnLoadingSubmit)}
        >
          {shipmentUnLoadingDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default ShipmentUnLoadingForm;